import { UNKNOWN_DATUM } from 'business/constants';
import { downloadFile } from 'helpers/helpers';
import { formatData } from 'helpers/formatHelper';
import { DataTypes as DT } from 'business/definitions';
import { ColumnTableT } from './csvValidators';

const UTF_8_BOM_BYTES = '\uFEFF';

const formatTime = (
    type: DataTypesT,
    value: Date | string | number,
): string => {
    let date = new Date();
    if (value instanceof Date) {
        date = value;
    } else {
        date = new Date(
            typeof value === 'number' ? value : parseInt(value, 10),
        );
        date.setTime(date.getTime() - date.getTimezoneOffset() * 60000);
    }
    switch (type) {
        case DT.date:
            return date.toLocaleDateString('fr-FR');
        case DT.datetime:
            return date.toLocaleDateString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        case DT.time: {
            const minutes = date.getMinutes();
            const hours = date.getUTCHours();
            return `${hours}:${minutes}:00`;
        }
        default:
            return '';
    }
};

const quoteValue = (value: string) => value.replaceAll('"', '""');

const getFormattedData = (value: string | number | Date, type: DataTypesT) => {
    const formattedData = formatData(value, type);
    if (formattedData == null || formattedData === UNKNOWN_DATUM) {
        return '';
    }
    switch (type) {
        // We need special case for the different times.
        case DT.date:
        case DT.datetime:
        case DT.time: {
            return formatTime(type, formattedData);
        }
        case DT.text:
        case DT.category:
            return typeof formattedData === 'string' &&
                (formattedData.includes(',') || formattedData.includes('"'))
                ? `"${quoteValue(formattedData)}"`
                : formattedData;
        default:
            return formattedData;
    }
};

const getDataRow = (
    columns: ColumnTableT[],
    data: ResponseDataT[],
    isFailedRows: boolean = false,
): (string | number)[][] => {
    const columnDict: { [key: string]: DataTypesT } = {};
    columns.forEach((c) => (columnDict[c.key] = c.type));
    return data.map((row) =>
        columns
            .filter((column) => row[column.key] !== null)
            .map((column): string | number => {
                const columnName = column.key;
                const value = row[columnName] as string | number;
                const type = columnDict[columnName];
                if (isFailedRows) {
                    return value;
                }

                return getFormattedData(value, type);
            }),
    );
};

const generateFakeData = (
    columns: ColumnTableT[],
): { [key: string]: number | string } => {
    const fakeData: { [column: string]: number | string } = {};
    columns.forEach((column) => {
        if (
            column.type === DT.date ||
            column.type === DT.datetime ||
            column.type === DT.time
        ) {
            fakeData[column.key] = new Date('2000-12-31').getTime();
        } else if (column.type === DT.float) {
            fakeData[column.key] = '2.5';
        } else if (column.type === DT.integer) {
            fakeData[column.key] = '2';
        } else {
            fakeData[column.key] = UNKNOWN_DATUM;
        }
    });
    return fakeData;
};

export const downloadModel = (
    fileName: string,
    columns: ColumnTableT[],
    data: ResponseDataT[],
    isFailedData: boolean = false,
): void => {
    const rowData = data.length > 0 ? data : [generateFakeData(columns)];
    const rows = [
        columns.map((c) => c.label),
        ...getDataRow(columns, rowData, isFailedData),
    ];
    // Prepend BOM for UTF-8 compatibility, especially with Excel
    const csvData = UTF_8_BOM_BYTES + rows.map((e) => e.join(';')).join('\n');
    const csvContent = `data:text/csv;charset=utf-8,${csvData}`;

    const encodedUri = encodeURI(csvContent);
    downloadFile(encodedUri, fileName, 'csv');
};
